import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { format, isSameDay } from "date-fns";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonItem,
  IonAvatar,
  IonList,
  IonTextarea,
} from "@ionic/react";
import { paperPlaneOutline } from "ionicons/icons";
import { parseNotes } from "../../functions/FX_ParseAndMap";
import { useChatMessages } from "../../hooks/useChatMessages";
import { sendSupportMonkeyMessage } from "../../utils/chatUtils";
import { SUPPORT_MONKEY_UID, SUPPORT_MONKEY_ASSOCIATE } from "../../constants/supportConstants";
import { AdminJobViewerModal } from "./AdminJobViewerModal";
import { collection, query as firestoreQuery, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { FB_COLLECTION_PATH } from "../../functions/postActionTypes";

interface SupportMonkeyChatPanelProps {
  chatId: string | null;
  chatMeta: any | null;
}

export const SupportMonkeyChatPanel: React.FC<SupportMonkeyChatPanelProps> = ({ chatId, chatMeta }) => {
  const [sendMessage, setSendMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [jobViewerState, setJobViewerState] = useState<{
    isOpen: boolean;
    uniqueJobId: string;
    schedUid: string;
    viewType: "werker" | "scheduler";
    werkerUid?: string;
  } | null>(null);
  const [jobMetadata, setJobMetadata] = useState<Map<string, any>>(new Map());
  const listRef = useRef<HTMLIonListElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement | null>(null);

  const { renderMessages, setPendingMessages } = useChatMessages(chatId);

  // Query v3 messages subcollection for job support metadata
  useEffect(() => {
    if (!chatId) {
      setJobMetadata(new Map());
      return;
    }

    const messagesRef = collection(db, FB_COLLECTION_PATH.Chats, chatId, "messages");
    const unsubscribe = onSnapshot(
      firestoreQuery(messagesRef),
      (snapshot) => {
        const metaMap = new Map();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.jobSupportMeta && data.clientId) {
            metaMap.set(data.clientId, data.jobSupportMeta);
          }
        });
        setJobMetadata(metaMap);
      },
      (error) => {
        console.error("Error fetching v3 messages:", error);
      }
    );

    return () => unsubscribe();
  }, [chatId]);

  const makeClientId = () => `${SUPPORT_MONKEY_UID}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      const lastItem = listRef.current.lastElementChild;
      if (lastItem) {
        lastItem.scrollIntoView({ behavior: "auto", block: "end" });
      }
    }
  }, []);

  const lastRenderMessageId = useMemo(() => {
    if (renderMessages.length === 0) return null;
    return renderMessages[renderMessages.length - 1]?.id;
  }, [renderMessages]);

  useEffect(() => {
    if (lastRenderMessageId) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [lastRenderMessageId, scrollToBottom]);

  const postMessage = async () => {
    if (!chatId) return;
    const text = (sendMessage || "").trim();
    if (!text) return;

    const clientId = makeClientId();
    const now = Date.now();
    setPendingMessages((prev) => [
      ...prev,
      {
        id: clientId,
        clientId,
        pending: true,
        failed: false,
        IsRead: true,
        MessageAuthorId: SUPPORT_MONKEY_UID,
        MessageAuthor: `${SUPPORT_MONKEY_ASSOCIATE.WerkerFirstName} ${SUPPORT_MONKEY_ASSOCIATE.WerkerLastName}`,
        MessageText: text,
        JobDocId: chatId,
        UserActionTakenId: 0,
        UserActionTakenUserId: 0,
        createdAt: now,
        updatedAt: now,
        MessageAuthorPic: SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL,
        NewChatPath: "",
      },
    ]);

    setSendMessage("");
    setIsSending(true);

    try {
      await sendSupportMonkeyMessage({
        chatId,
        message: text,
        chatMeta,
        clientId,
      });
    } catch (error) {
      console.error("Error sending support monkey message:", error);
      setPendingMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m))
      );
    } finally {
      setIsSending(false);
    }
  };

  if (!chatId) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          opacity: 0.85,
          gap: 10,
        }}
      >
        <h3 style={{ margin: 0 }}>Support Monkey Inbox</h3>
        <p style={{ color: "var(--ion-color-medium)", margin: 0 }}>Select a chat to view messages.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--ion-color-light-shade)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: "var(--ion-color-light)",
        }}
      >
        <IonAvatar style={{ width: "40px", height: "40px" }}>
          <img
            src={chatMeta?.SendersPic || "../assets/profilePic.png"}
            alt=""
            onError={(e: any) => {
              e.target.src = "../assets/profilePic.png";
            }}
          />
        </IonAvatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>
            {chatMeta?.MessageTitle || "Support Chat"}
          </h3>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "var(--ion-background-color)",
        }}
      >
        {renderMessages.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--ion-color-medium)" }}>
            <h4>Start chatting ...</h4>
          </div>
        )}

        {renderMessages.length > 0 && (
          <IonList
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              padding: "0px",
              backgroundColor: "transparent",
            }}
            ref={listRef}
          >
            {renderMessages.map((message, index, arr) => {
              const prevMessage = arr[index - 1];
              const isNewDay =
                !prevMessage || !isSameDay(new Date(message.createdAt), new Date(prevMessage.createdAt));

              // Get job support metadata from v3 messages
              const meta = message.clientId ? jobMetadata.get(message.clientId) : null;
              const displayText = message.MessageText;
              
              // Build admin details section if this is a support request from a user
              let adminDetailsText = "";
              if (meta && message.MessageAuthorId !== SUPPORT_MONKEY_UID) {
                adminDetailsText = `\n\n🔧 Admin Details:\n• Scheduler UID: ${meta.schedUid}\n• Unique Job ID: ${meta.uniqueJobId}${meta.docId ? `\n• Doc ID: ${meta.docId}` : ""}`;
              }

              return (
                <React.Fragment key={message.id}>
                  {isNewDay && (
                    <div
                      style={{
                        textAlign: "center",
                        margin: "10px 0",
                        marginTop: "25px",
                        color: "#92949c",
                        fontWeight: "bold",
                      }}
                    >
                      {format(new Date(message.createdAt), "MMMM d, yyyy")}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexDirection: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: "10px",
                      margin: "12px",
                    }}
                  >
                    {message.MessageAuthorId !== SUPPORT_MONKEY_UID && (
                      <IonAvatar
                        style={{
                          marginLeft: "-12px",
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      >
                        <img src={message.MessageAuthorPic} alt="" />
                      </IonAvatar>
                    )}

                    <div
                      style={{
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        position: "relative",
                        alignSelf: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "flex-end" : "flex-start",
                        backgroundColor: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "#E0742E" : "#e8e8e8",
                        color: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "#000000" : "#1e1e1e",
                        maxWidth: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "80%" : "75%",
                        padding: "10px 15px",
                        border: "1px solid",
                        borderRadius: "10px",
                        borderBottomRightRadius: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "0px" : "10px",
                        borderBottomLeftRadius: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "10px" : "0px",
                        minWidth: "55px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#6d6e73" }}>
                        {message.MessageAuthorId !== SUPPORT_MONKEY_UID && <span>{message.MessageAuthor}</span>}
                      </span>
                      <p style={{ margin: 0, userSelect: "text" }}>{parseNotes(displayText + adminDetailsText)}</p>
                      <span
                        style={{
                          fontSize: "12px",
                          minWidth: "55px",
                          color: "#92949c",
                          position: "absolute",
                          bottom: "-18px",
                          right: "0",
                        }}
                      >
                        {message.pending ? "Sending..." : format(new Date(message.createdAt), "h:mm a")}
                      </span>
                    </div>
                  </div>
                  {meta && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: message.MessageAuthorId === SUPPORT_MONKEY_UID ? "flex-end" : "flex-start",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        marginTop: "-2px",
                        marginBottom: "6px",
                      }}
                    >
                      <IonButton
                        size="small"
                        fill="outline"
                        color="medium"
                        style={{ "--border-radius": "8px", fontSize: "0.8rem", height: "28px" } as any}
                        onClick={() =>
                          setJobViewerState({
                            isOpen: true,
                            uniqueJobId: meta.uniqueJobId,
                            schedUid: meta.schedUid,
                            viewType: meta.viewType,
                            werkerUid: meta.werkerUid,
                          })
                        }
                      >
                        🔍 View Job
                      </IonButton>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <br />
          </IonList>
        )}
      </div>

      <div
        style={{
          padding: "16px",
          borderTop: "1px solid var(--ion-color-light-shade)",
          backgroundColor: "var(--ion-color-light)",
        }}
      >
        <IonGrid style={{ padding: 0 }}>
          <IonRow>
            <IonCol size="10">
              <IonItem counter={true} lines="none">
                <IonTextarea
                  ref={textareaRef}
                  spellCheck={true}
                  autoCapitalize="sentences"
                  autoGrow={true}
                  rows={1}
                  maxlength={1000}
                  enterkeyhint="send"
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void postMessage();
                    }
                  }}
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                  value={sendMessage}
                  placeholder="Send Message..."
                  onIonChange={(e) => setSendMessage(e.detail.value || "")}
                />
              </IonItem>
            </IonCol>

            <IonCol size="2">
              <IonButton
                type="button"
                fill="clear"
                disabled={isSending}
                onPointerDown={(e) => {
                  e.preventDefault();
                }}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => void postMessage()}
              >
                <IonIcon size="small" icon={paperPlaneOutline} />
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>

      <AdminJobViewerModal
        isOpen={jobViewerState?.isOpen ?? false}
        onDismiss={() => setJobViewerState(null)}
        uniqueJobId={jobViewerState?.uniqueJobId ?? ""}
        schedUid={jobViewerState?.schedUid ?? ""}
        viewType={jobViewerState?.viewType ?? "scheduler"}
        werkerUid={jobViewerState?.werkerUid}
      />
    </div>
  );
};
